"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Instagram, Twitter, Globe, Mail, ArrowRight, Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const socialLinks = [{
  name: "LinkedIn",
  url: "https://linkedin.com",
  icon: Globe
}, {
  name: "Twitter",
  url: "https://twitter.com",
  icon: Twitter
}, {
  name: "Instagram",
  url: "https://instagram.com",
  icon: Instagram
}];
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-500">
        <div className="size-16 rounded-md bg-primary/10 flex items-center justify-center text-primary mb-4">
          <Mail className="size-8" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-primary/30 hover:bg-primary/5 text-foreground rounded-md">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-default font-semibold uppercase tracking-wider text-xs">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Johnathan Smith" {...field} className="border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary bg-transparent px-0" />
              </FormControl>
              <FormDescription className="text-[10px] font-mono opacity-70">Legal name for documentation</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-default font-semibold uppercase tracking-wider text-xs">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jsmith@example.com" {...field} className="border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary bg-transparent px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-default font-semibold uppercase tracking-wider text-xs">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary bg-transparent px-0" />
              </FormControl>
              <FormDescription className="text-[10px] font-mono opacity-70">Direct contact line</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="inquiry_type" rules={{
        required: "Please select a loan type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-default font-semibold uppercase tracking-wider text-xs">Loan Solution</FormLabel>
              <FormControl>
                <div className="relative">
                  <select {...field} className="flex h-9 w-full appearance-none border-0 border-b border-border bg-transparent px-0 py-1 text-base transition-colors outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-foreground">
                    <option value="New Purchase" className="bg-card text-foreground">New Home Purchase</option>
                    <option value="Refinance" className="bg-card text-foreground">Refinancing</option>
                    <option value="Home Equity" className="bg-card text-foreground">Home Equity Line</option>
                    <option value="Commercial" className="bg-card text-foreground">Commercial Lending</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-primary">
                    <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-default font-semibold uppercase tracking-wider text-xs">Loan Amount (USD)</FormLabel>
              <FormControl>
                <Input type="number" min="0" placeholder="e.g., 450000" {...field} className="border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary bg-transparent px-0" />
              </FormControl>
              <FormDescription className="text-[10px] font-mono opacity-70">Estimated principal amount</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="deadline" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-default font-semibold uppercase tracking-wider text-xs">Property Zip Code</FormLabel>
              <FormControl>
                <Input placeholder="90210" {...field} className="border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary bg-transparent px-0" />
              </FormControl>
              <FormDescription className="text-[10px] font-mono opacity-70">Location of the asset</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="details" rules={{
      required: "Please provide additional context"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-default font-semibold uppercase tracking-wider text-xs">Additional Context</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your financial goals or specific property details..." className="min-h-[100px] resize-y border-0 border-b border-border focus-visible:ring-0 focus-visible:border-primary bg-transparent px-0" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm font-default">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full mt-6 bg-primary text-primary-foreground hover:bg-primary/90 h-14 text-lg font-default font-bold uppercase tracking-widest rounded-md shadow-lg transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Processing...
          </> : <>
            Submit Inquiry
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function LetSTalkFinance() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="222205" id="lets-talk-finance" className="bg-background text-foreground py-24 px-4 md:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          
          {}
          <div ref={leftRef} className={`lg:col-span-4 space-y-10 transition-all duration-1000 ease-out ${isLeftVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="space-y-6">
              <h2 className="text-5xl md:text-6xl font-default font-bold tracking-tighter text-primary leading-none">
                Guided <br />
                <span className="text-secondary italic font-default font-light">Clarity</span>
              </h2>
              <Separator className="w-16 bg-primary h-1.5" />
              <p className="text-xl text-foreground/80 leading-relaxed font-default">
                Secure your future with Beacon Finance Group. Our advisors are ready to architect your path to homeownership.
              </p>
            </div>

            <div className="space-y-6 pt-4">
              <h3 className="text-xs font-mono font-bold uppercase tracking-widest text-primary/60">Advisory Hours</h3>
              <div className="space-y-4 border-l-2 border-muted pl-6">
                <div className="flex justify-between items-center">
                  <span className="font-default font-medium text-foreground">Mon — Fri</span>
                  <span className="font-mono text-sm text-primary">08:00 - 18:00</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-default font-medium text-foreground">Saturday</span>
                  <span className="font-mono text-sm text-primary">10:00 - 14:00</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-default font-medium text-foreground">Sunday</span>
                  <span className="font-mono text-sm text-primary">Closed</span>
                </div>
              </div>
            </div>

            <div className="space-y-6 pt-4">
              <h3 className="text-xs font-mono font-bold uppercase tracking-widest text-primary/60">Direct Lines</h3>
              <div className="flex flex-col space-y-4">
                {socialLinks.map((social, i) => <Link key={social.name} to={social.url} newTab className="group flex items-center space-x-4 text-foreground hover:text-primary transition-colors duration-300 w-fit" data-index={i}>
                    <div className="size-10 rounded-md border border-border bg-card flex items-center justify-center group-hover:border-primary/50 group-hover:bg-primary/5 transition-all duration-300 shadow-sm">
                      <social.icon className="size-4 text-primary" />
                    </div>
                    <span className="font-default font-semibold text-sm uppercase tracking-wider">{social.name}</span>
                  </Link>)}
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-8 transition-all duration-1000 delay-200 ease-out ${isRightVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Card className="bg-card border-0 shadow-4xl overflow-hidden relative rounded-md">
              {}
              <div className="absolute -top-40 -right-40 size-80 bg-primary/5 rounded-lg blur-[100px] pointer-events-none" />
              <div className="absolute -bottom-40 -left-40 size-80 bg-secondary/5 rounded-lg blur-[100px] pointer-events-none" />
              
              <CardHeader className="pb-8 relative z-10 border-b border-muted">
                <CardTitle className="text-3xl font-default font-bold text-primary uppercase tracking-tight">Lending Inquiry</CardTitle>
                <CardDescription className="text-base font-default text-foreground/60">
                  Provide your details below for a personalized mortgage assessment. Response time: &lt; 24 hours.
                </CardDescription>
              </CardHeader>
              <CardContent className="relative z-10 pt-10">
                <FormProvider formKey="talk_finance_form_contact" formId="talk_finance_form_contact" sectionName="lets-talk-finance" defaultValues={{
                name: "",
                email: "",
                phone: "",
                inquiry_type: "New Purchase",
                details: "",
                budget: "",
                deadline: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                inquiry_type: {
                  required: true
                },
                details: {
                  required: true
                }
              }} submitText="Submit Inquiry" successMessage="Your financial inquiry has been logged. A Beacon advisor will contact you shortly.">
                  <ContactForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}